import apiClient from './apiClient';
import { User, ApiResponse, PaginationParams } from '@/types';

interface UserFilters {
  search?: string;
  role?: string;
  isActive?: boolean;
  createdFrom?: string;
  createdTo?: string;
}

interface CreateUserData {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  role: 'admin' | 'operator' | 'viewer';
  phone?: string;
}

interface UpdateUserData {
  email?: string;
  first_name?: string;
  last_name?: string;
  role?: 'admin' | 'operator' | 'viewer';
  phone?: string;
  is_active?: boolean;
}

interface UserStats {
  total: number;
  active: number;
  inactive: number;
  newThisMonth: number;
  byRole: {
    admin: number;
    operator: number;
    viewer: number;
  };
}

class UserService {
  async getUsers(params?: PaginationParams & UserFilters): Promise<ApiResponse<User[]>> {
    const response = await apiClient.get('/users', { params });
    return response.data;
  }

  async getUser(userId: number): Promise<User> {
    const response = await apiClient.get(`/users/${userId}`);
    return response.data;
  }

  async createUser(data: CreateUserData): Promise<User> {
    const response = await apiClient.post('/users', data);
    return response.data;
  }

  async updateUser(userId: number, data: UpdateUserData): Promise<User> {
    const response = await apiClient.put(`/users/${userId}`, data);
    return response.data;
  }

  async deleteUser(userId: number): Promise<void> {
    await apiClient.delete(`/users/${userId}`);
  }

  async activateUser(userId: number): Promise<User> {
    const response = await apiClient.post(`/users/${userId}/activate`);
    return response.data;
  }

  async deactivateUser(userId: number): Promise<User> {
    const response = await apiClient.post(`/users/${userId}/deactivate`);
    return response.data;
  }

  async resetUserPassword(userId: number): Promise<{ temporaryPassword: string }> {
    const response = await apiClient.post(`/users/${userId}/reset-password`);
    return response.data;
  }

  async getUserStats(): Promise<UserStats> {
    const response = await apiClient.get('/users/stats');
    return response.data;
  }

  async exportUsers(format: 'csv' | 'excel', filters?: UserFilters): Promise<Blob> {
    const response = await apiClient.post(
      '/users/export',
      { format, filters },
      { responseType: 'blob' }
    );
    return response.data;
  }

  async importUsers(file: File): Promise<{ imported: number; errors: any[] }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post('/users/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getUserActivity(userId: number, days: number = 30): Promise<any[]> {
    const response = await apiClient.get(`/users/${userId}/activity`, {
      params: { days },
    });
    return response.data;
  }

  async getUserEmergencies(userId: number, params?: PaginationParams): Promise<any> {
    const response = await apiClient.get(`/users/${userId}/emergencies`, { params });
    return response.data;
  }

  async getUserHealthRecords(userId: number, params?: PaginationParams): Promise<any> {
    const response = await apiClient.get(`/users/${userId}/health-records`, { params });
    return response.data;
  }

  async getUserEmergencyContacts(userId: number): Promise<any[]> {
    const response = await apiClient.get(`/users/${userId}/emergency-contacts`);
    return response.data;
  }

  async updateUserEmergencyContacts(userId: number, contacts: any[]): Promise<any[]> {
    const response = await apiClient.put(`/users/${userId}/emergency-contacts`, { contacts });
    return response.data;
  }

  async getUserPermissions(userId: number): Promise<string[]> {
    const response = await apiClient.get(`/users/${userId}/permissions`);
    return response.data;
  }

  async updateUserPermissions(userId: number, permissions: string[]): Promise<string[]> {
    const response = await apiClient.put(`/users/${userId}/permissions`, { permissions });
    return response.data;
  }

  // Profile methods
  async getProfile(): Promise<User> {
    const response = await apiClient.get('/profile');
    return response.data;
  }

  async updateProfile(data: UpdateUserData): Promise<User> {
    const response = await apiClient.put('/profile', data);
    return response.data;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await apiClient.post('/profile/change-password', {
      currentPassword,
      newPassword,
    });
  }

  async uploadAvatar(file: File): Promise<{ avatarUrl: string }> {
    const formData = new FormData();
    formData.append('avatar', file);

    const response = await apiClient.post('/profile/avatar', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async deleteAvatar(): Promise<void> {
    await apiClient.delete('/profile/avatar');
  }

  async getLoginHistory(limit: number = 10): Promise<any[]> {
    const response = await apiClient.get('/profile/login-history', {
      params: { limit },
    });
    return response.data;
  }

  async enable2FA(): Promise<{ qrCode: string; secret: string }> {
    const response = await apiClient.post('/profile/2fa/enable');
    return response.data;
  }

  async disable2FA(code: string): Promise<void> {
    await apiClient.post('/profile/2fa/disable', { code });
  }

  async verify2FA(code: string): Promise<void> {
    await apiClient.post('/profile/2fa/verify', { code });
  }

  async getNotificationPreferences(): Promise<any> {
    const response = await apiClient.get('/profile/notification-preferences');
    return response.data;
  }

  async updateNotificationPreferences(preferences: any): Promise<any> {
    const response = await apiClient.put('/profile/notification-preferences', preferences);
    return response.data;
  }
}

export const userService = new UserService();
